// src/lib/passwordReset.ts
import { supabase } from '@/lib/supabase';
import { turso } from '@/database/client';
import bcrypt from 'bcryptjs';
import { checkRateLimit, RateLimitPresets } from './rateLimiter';

interface PasswordResetToken {
  token: string;
  user_id: string;
  expires_at: string;
  used: boolean;
}

/**
 * Request a password reset using SendGrid + Turso token storage
 */
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string; debugResetLink?: string }> {
  try {
    const isAllowed = checkRateLimit({
      ...RateLimitPresets.passwordReset,
      identifier: `reset_${email}`
    });

    if (!isAllowed) {
      return {
        success: false,
        error: 'Too many reset attempts. Please try again later.'
      };
    }

    await initPasswordResetTable();

    // Look up user but never reveal if they exist
    const userResult = await turso.execute({
      sql: 'SELECT id FROM users WHERE email = ? LIMIT 1',
      args: [email]
    });

    const hasUser = !!(userResult.rows && userResult.rows.length > 0);
    const userId = hasUser ? (userResult.rows[0].id as string) : null;

    let resetLink = `${window.location.origin}/reset-password`;

    // Only create a Turso token if we have a matching user
    if (hasUser) {
      await turso.execute({
        sql: 'DELETE FROM password_reset_tokens WHERE user_id = ?',
        args: [userId]
      });

      const token = generateSecureToken();
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();

      await turso.execute({
        sql: `INSERT INTO password_reset_tokens (token, user_id, expires_at, used, created_at)
              VALUES (?, ?, ?, 0, datetime('now'))`,
        args: [token, userId, expiresAt]
      });

      resetLink = `${window.location.origin}/reset-password?token=${token}`;
    }

    const isYahooFamily = /@(yahoo\.com|ymail\.com|rocketmail\.com)$/i.test(email);
    const devMode = typeof localStorage !== 'undefined' && localStorage.getItem('whipt_dev_mode') === 'true';

    // REVERT: Use custom Edge Function with SendGrid (back to original working version)
    let sendgridSent = false;
    if (hasUser) {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        console.log('[PasswordReset] Sending email via WHIPT Edge Function...');
        const sgResp = await fetch(`${supabaseUrl}/functions/v1/whipt-password-reset`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({
            to: email,
            resetLink
          })
        });
        sendgridSent = sgResp.ok;
        if (!sgResp.ok) {
          const errText = await sgResp.text().catch(() => '');
          console.error('[PasswordReset] SendGrid Edge Function failed', sgResp.status, errText);
        } else {
          console.log('[PasswordReset] ✅ Email sent successfully');
        }
      } catch (err) {
        console.error('[PasswordReset] SendGrid Edge Function error', err);
      }
    }

    if (!sendgridSent) {
      return {
        success: false,
        error: 'Failed to send reset email. Please try again.'
      };
    }

    // Provide manual link in dev mode only when we have a Turso token
    const debugLink = devMode && hasUser ? resetLink : undefined;
    return debugLink ? { success: true, debugResetLink: debugLink } : { success: true };
  } catch (error) {
    console.error('[PasswordReset] Failed to request reset:', error);
    return {
      success: false,
      error: 'Failed to process password reset request'
    };
  }
}

/**
 * Verify a password reset token
 */
export async function verifyResetToken(token: string): Promise<{ valid: boolean; userId?: string; error?: string }> {
  try {
    const result = await turso.execute({
      sql: `SELECT user_id, expires_at, used FROM password_reset_tokens 
            WHERE token = ? LIMIT 1`,
      args: [token]
    });

    if (!result.rows || result.rows.length === 0) {
      return {
        valid: false,
        error: 'Invalid reset token'
      };
    }

    const tokenData = result.rows[0];
    const expiresAt = new Date(tokenData.expires_at as string);
    const used = tokenData.used as number;

    if (used === 1) {
      return {
        valid: false,
        error: 'Reset token has already been used'
      };
    }

    if (new Date() > expiresAt) {
      return {
        valid: false,
        error: 'Reset token has expired'
      };
    }

    return {
      valid: true,
      userId: tokenData.user_id as string
    };
  } catch (error) {
    console.error('[PasswordReset] Failed to verify token:', error);
    return {
      valid: false,
      error: 'Failed to verify reset token'
    };
  }
}

/**
 * Reset password with token
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate password strength
    const { validatePasswordStrength } = await import('./auth');
    const validation = validatePasswordStrength(newPassword);
    
    if (!validation.isValid) {
      return {
        success: false,
        error: `Password requirements not met:\n${validation.errors.join('\n')}`
      };
    }

    // Verify token
    const verification = await verifyResetToken(token);
    if (!verification.valid || !verification.userId) {
      return {
        success: false,
        error: verification.error || 'Invalid token'
      };
    }

    // Hash new password
    const passwordHash = await bcrypt.hash(newPassword, 12);

    // Update password
    await turso.execute({
      sql: 'UPDATE users SET password_hash = ?, last_active_at = datetime(\'now\') WHERE id = ?',
      args: [passwordHash, verification.userId]
    });

    // Mark token as used
    await turso.execute({
      sql: 'UPDATE password_reset_tokens SET used = 1 WHERE token = ?',
      args: [token]
    });

    // Invalidate all sessions for this user (force re-login)
    await turso.execute({
      sql: 'DELETE FROM sessions WHERE user_id = ?',
      args: [verification.userId]
    });

    console.log(`[PasswordReset] Password reset successful for user ${verification.userId}`);

    return {
      success: true
    };
  } catch (error) {
    console.error('[PasswordReset] Failed to reset password:', error);
    return {
      success: false,
      error: 'Failed to reset password'
    };
  }
}

/**
 * Change password (when user is logged in)
 */
export async function changePassword(
  userId: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate password strength
    const { validatePasswordStrength } = await import('./auth');
    const validation = validatePasswordStrength(newPassword);
    
    if (!validation.isValid) {
      return {
        success: false,
        error: `Password requirements not met:\n${validation.errors.join('\n')}`
      };
    }

    // Verify current password
    const userResult = await turso.execute({
      sql: 'SELECT password_hash FROM users WHERE id = ? LIMIT 1',
      args: [userId]
    });

    if (!userResult.rows || userResult.rows.length === 0) {
      return {
        success: false,
        error: 'User not found'
      };
    }

    const currentHash = userResult.rows[0].password_hash as string;
    const isValid = await bcrypt.compare(currentPassword, currentHash);

    if (!isValid) {
      return {
        success: false,
        error: 'Current password is incorrect'
      };
    }

    // Hash new password
    const passwordHash = await bcrypt.hash(newPassword, 12);

    // Update password
    await turso.execute({
      sql: 'UPDATE users SET password_hash = ?, last_active_at = datetime(\'now\') WHERE id = ?',
      args: [passwordHash, userId]
    });

    // Invalidate all other sessions (keep current one)
    // This is handled by the caller

    console.log(`[PasswordChange] Password changed successfully for user ${userId}`);

    return {
      success: true
    };
  } catch (error) {
    console.error('[PasswordChange] Failed to change password:', error);
    return {
      success: false,
      error: 'Failed to change password'
    };
  }
}

/**
 * Generate a secure random token
 */
function generateSecureToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Initialize password reset table
 */
export async function initPasswordResetTable(): Promise<void> {
  try {
    await turso.execute(`
      CREATE TABLE IF NOT EXISTS password_reset_tokens (
        token TEXT PRIMARY KEY,
        user_id TEXT NOT NULL,
        expires_at TEXT NOT NULL,
        used INTEGER DEFAULT 0,
        created_at TEXT NOT NULL,
        FOREIGN KEY (user_id) REFERENCES users(id) ON DELETE CASCADE
      )
    `);

    // Create index for cleanup
    await turso.execute(`
      CREATE INDEX IF NOT EXISTS idx_reset_tokens_expires 
      ON password_reset_tokens(expires_at)
    `);

    console.log('[PasswordReset] Table initialized');
  } catch (error) {
    console.error('[PasswordReset] Failed to initialize table:', error);
  }
}

/**
 * Clean up expired tokens (call periodically)
 */
export async function cleanupExpiredTokens(): Promise<number> {
  try {
    const result = await turso.execute({
      sql: 'DELETE FROM password_reset_tokens WHERE expires_at < datetime(\'now\') OR used = 1',
      args: []
    });

    const deleted = result.rowsAffected || 0;
    if (deleted > 0) {
      console.log(`[PasswordReset] Cleaned up ${deleted} expired/used tokens`);
    }

    return deleted;
  } catch (error) {
    console.error('[PasswordReset] Failed to cleanup tokens:', error);
    return 0;
  }
}
